import { Anchor, Button, Stack, Table, Text, TextInput } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCheck, IconMail, IconShoppingCart } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { getCloudMetadata, getCurrentUser } from "../shared/getCurrentUser";
import { getErrorAsString } from "../shared/logging/getErrorAsString";
import { Log } from "../shared/logging/Log";
import { Login } from "../shared/Login";
import { IDistributorInfo, queryKeys, RestApi } from "../shared/RestApi";
import { HowManyLicencesHaveYouMessage } from "./HowManyLicencesHaveYou";
import { HowManyLicencesYouHaveMessage } from "./HowManyLicencesYouHaveMessage";
import { NavBarContainer } from "./NavBarContainer";
import { NeedToRegister } from "./NeedToRegister";
// eslint-disable-next-line @typescript-eslint/no-require-imports
const dinlabeldistribuidores = require("url:../assets/DinLabelDistribuidores.pdf");

function openAlertModal(options: {
  title: string;
  message: string;
  color?: string;
  onClose?: () => void;
}) {
  modals.open({
    title: options.title,
    centered: true,
    onClose: options.onClose,
    children: (
      <Stack gap="md">
        <Text size="sm">{options.message}</Text>
        <Button
          color={options.color}
          onClick={() => {
            modals.closeAll();
          }}
        >
          OK
        </Button>
      </Stack>
    ),
  });
}

export function PortalDeDistribuidores() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const userInfo = getCurrentUser();

  const {
    data: distributorInfo,
    isPending,
    isError,
  } = useQuery({
    queryKey: queryKeys.distributorInfo,
    queryFn: () => RestApi.getDistributorInfo(),
    enabled: !!userInfo,
  });

  const resendMutation = useMutation({
    mutationFn: ({
      rowKey,
      partitionKey,
      email,
    }: {
      rowKey: string;
      partitionKey: string;
      email: string;
    }) => RestApi.resendLicencia(rowKey, partitionKey, email),
  });

  const sendMutation = useMutation({
    mutationFn: (mail: string) => RestApi.sendLicencia(mail),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.distributorInfo });
    },
  });

  const resendLicense = (
    rowKey: string,
    partitionKey: string,
    email: string,
  ) => {
    if (!distributorInfo || !userInfo) {
      throw new Error("Distributor info should be defined");
    }

    Log.logger.info("Enviando licencia...");
    modals.openConfirmModal({
      title: t("licenses.sendTitle"),
      children: (
        <Text size="sm">{t("licenses.resendConfirm", { email })}</Text>
      ),
      labels: {
        confirm: t("dialogs.confirmYes"),
        cancel: t("dialogs.cancel"),
      },
      centered: true,
      onConfirm: () => {
        void (async () => {
          try {
            await resendMutation.mutateAsync({ rowKey, partitionKey, email });
          } catch (error) {
            openAlertModal({
              title: t("licenses.errorTitle"),
              message: t("licenses.sendError", {
                error: getErrorAsString(error),
              }),
              color: "red",
            });
            return;
          }

          openAlertModal({
            title: t("licenses.resentTitle"),
            message: t("licenses.resentMessage", {
              email,
              cc: distributorInfo.CloudUser.AccountEmail,
            }),
            color: "blue",
          });
        })();
      },
    });
  };

  const onEnviarLicenciasClick = () => {
    if (!distributorInfo || !userInfo) {
      throw new Error("Distributor info should be defined");
    }

    Log.logger.info("Enviando licencia...");
    let emailValue = "";

    modals.openConfirmModal({
      title: t("licenses.sendTitle"),
      children: (
        <>
          <Text size="sm" mb="sm">
            {t("licenses.sendPrompt")}
          </Text>
          <TextInput
            placeholder={t("licenses.emailPlaceholder")}
            data-autofocus
            onChange={(event) => {
              emailValue = event.currentTarget.value;
            }}
          />
        </>
      ),
      labels: {
        confirm: t("dialogs.send"),
        cancel: t("dialogs.cancel"),
      },
      centered: true,
      onConfirm: () => {
        const mail = emailValue.trim();
        if (!mail) {
          return;
        }

        void (async () => {
          try {
            await sendMutation.mutateAsync(mail);
          } catch (error) {
            openAlertModal({
              title: t("licenses.errorTitle"),
              message: t("licenses.sendError", {
                error: getErrorAsString(error),
              }),
              color: "red",
            });
            return;
          }

          openAlertModal({
            title: t("licenses.sentTitle"),
            message: t("licenses.sentMessage", {
              email: mail,
              cc: distributorInfo.CloudUser.AccountEmail,
            }),
            color: "blue",
          });
        })();
      },
    });
  };

  const renderSendLicenseDialogButton = (
    info: IDistributorInfo,
  ): React.JSX.Element | null => {
    if (info.AvailableLicenses <= 0) {
      return null;
    }

    return (
      <Button
        leftSection={<IconMail size={16} />}
        onClick={onEnviarLicenciasClick}
        loading={sendMutation.isPending}
        w="fit-content"
      >
        {t("licenses.sendToEmail")}
      </Button>
    );
  };

  const renderLicensesSentTable = (
    info: IDistributorInfo,
  ): React.JSX.Element | null => {
    if (info.DistributorLicenseMailMappings.length <= 0) {
      return null;
    }

    return (
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("licenses.recipient")}</Table.Th>
            <Table.Th>{t("licenses.date")}</Table.Th>
            <Table.Th>{t("licenses.sent")}</Table.Th>
            <Table.Th>{t("licenses.activated")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {info.DistributorLicenseMailMappings.map((row) => {
            const sentDate = new Date(row.InsertionTime);
            return (
              <Table.Tr key={row.LicenseId}>
                <Table.Td>{row.LicenseInvitationEmail}</Table.Td>
                <Table.Td>
                  {sentDate.toLocaleDateString(i18n.language)}
                </Table.Td>
                <Table.Td>
                  <IconCheck size={18} color="var(--mantine-color-green-6)" />
                </Table.Td>
                <Table.Td>
                  {!row.AcceptedByUserId && (
                    <Stack gap="xs">
                      <Text size="sm">{t("licenses.notActivated")}</Text>
                      <Button
                        color="green"
                        size="xs"
                        w="fit-content"
                        loading={resendMutation.isPending}
                        onClick={() => {
                          resendLicense(
                            row.LicenseId,
                            info.CloudUser.UserId,
                            row.LicenseInvitationEmail,
                          );
                        }}
                      >
                        {t("licenses.resend")}
                      </Button>
                    </Stack>
                  )}
                  {row.AcceptedByUserId && (
                    <IconCheck size={18} color="var(--mantine-color-green-6)" />
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  };

  const renderAuthorizedContent = (
    info: IDistributorInfo,
  ): React.JSX.Element => {
    return (
      <Stack gap="sm">
        <HowManyLicencesYouHaveMessage distributorInfo={info} />
        {renderSendLicenseDialogButton(info)}
        <Button
          component={Link}
          to="/ComprarLicencias"
          leftSection={<IconShoppingCart size={16} />}
          w="fit-content"
        >
          {t("portal.buyLicenses")}
        </Button>
        <HowManyLicencesHaveYouMessage distributorInfo={info} />
        {renderLicensesSentTable(info)}
      </Stack>
    );
  };

  if (!userInfo) {
    return (
      <NavBarContainer>
        <Login />
      </NavBarContainer>
    );
  }

  if (isPending) {
    return (
      <NavBarContainer title={t("portal.title")}>
        <Text>{t("portal.loading")}</Text>
      </NavBarContainer>
    );
  }

  if (isError) {
    return (
      <NavBarContainer title={t("portal.title")}>
        <Text c="red">{t("portal.loadError")}</Text>
      </NavBarContainer>
    );
  }

  if (!distributorInfo) {
    return (
      <NavBarContainer>
        <NeedToRegister />
      </NavBarContainer>
    );
  }

  return (
    <NavBarContainer title={t("portal.title")}>
      <Stack gap="md">
        <Text>
          {t("portal.welcome", {
            name: userInfo.Name,
            company: distributorInfo.DistributorInfo.CompanyName,
            email: distributorInfo.CloudUser.AccountEmail,
          })}
        </Text>

        {distributorInfo.DistributorInfo.DistribuidorAutorizado &&
          renderAuthorizedContent(distributorInfo)}

        {!distributorInfo.DistributorInfo.DistribuidorAutorizado && (
          <Stack gap="sm">
            <Text>
              {t("portal.pendingApproval", {
                email: getCloudMetadata().DistributorSupportEmail,
              })}
            </Text>
            <Anchor href={dinlabeldistribuidores}>
              {t("portal.distributorInfo")}
            </Anchor>
          </Stack>
        )}
      </Stack>
    </NavBarContainer>
  );
}
